import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, useIsHost, useMyPlayer, useCurrentQuestion } from '../stores/gameStore';
import { useGameActions, useSocket } from '../hooks/useSocket';
import type { Question } from '@youraiquiz/shared';

const ResultView = () => {
  const { gameState, clientState } = useGameStore();
  const isHost = useIsHost();
  const myPlayer = useMyPlayer();
  const currentQuestion = useCurrentQuestion();
  const { nextQuestion, kickPlayer } = useGameActions();
  const { leaveRoom } = useSocket();

  const [confirmKickId, setConfirmKickId] = useState<string | null>(null);

  // Cache the question to prevent showing next question's answer during exit animation
  const cachedQuestionRef = useRef<Question | null>(null);

  useEffect(() => {
    // Only update cached question when we have a valid question and we're in ROUND_RESULT
    if (currentQuestion && gameState.status === 'ROUND_RESULT') {
      cachedQuestionRef.current = currentQuestion;
    }
  }, [currentQuestion, gameState.status]);

  const question = cachedQuestionRef.current || currentQuestion;

  if (!question) return null;

  const correct = question.options[question.correctIndex];
  const sortedPlayers = [...gameState.players].sort((a, b) => b.score - a.score);
  const myRank = sortedPlayers.findIndex(p => p.id === clientState.myId) + 1;
  const rankImproved = myPlayer && myPlayer.previousRank > 0 && myRank < myPlayer.previousRank;

  // Progress and next question info
  const currentIdx = gameState.currentQuestionIndex;
  const totalQuestions = gameState.questions.length;
  const hasMoreQuestions = currentIdx + 1 < totalQuestions;
  const upcomingQuestion = hasMoreQuestions ? gameState.questions[currentIdx + 1] : null;

  const handleKickClick = (id: string) => {
    if (confirmKickId === id) {
      kickPlayer(id);
      setConfirmKickId(null);
    } else {
      setConfirmKickId(id);
      setTimeout(() => setConfirmKickId(null), 3000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-full max-w-4xl flex flex-col items-center space-y-8"
    >
      {/* Leave Room Button */}
      <button
        onClick={leaveRoom}
        className="self-start text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-red-500 transition-colors"
      >
        LEAVE ROOM
      </button>

      {/* Progress Header */}
      <div className="w-full flex justify-between items-center px-4">
        <div className="text-gray-400 font-black uppercase tracking-widest text-xs">
          Question <span className="text-white text-lg">{currentIdx + 1}</span> / {totalQuestions}
        </div>
        {hasMoreQuestions && upcomingQuestion && (
          <div className="text-right">
            <span className="text-gray-500 font-bold text-[10px] uppercase tracking-wider">Next: </span>
            <span className="text-neon-blue font-black text-sm">{upcomingQuestion.category}</span>
          </div>
        )}
      </div>

      {/* Correct Answer */}
      <div className="text-center space-y-2">
        <h2 className="text-gray-400 font-black uppercase tracking-widest text-xs">Correct Answer</h2>
        <motion.div
          initial={{ scale: 0.8, y: -20 }}
          animate={{ scale: 1, y: 0 }}
          className="bg-green-500 text-white text-3xl md:text-5xl font-black px-12 py-6 rounded-3xl shadow-[0_0_30px_rgba(34,197,94,0.4)] border-4 border-green-400"
        >
          {correct}
        </motion.div>
      </div>

      {/* Scoreboard */}
      <div className="w-full space-y-4">
        <div className="flex justify-between items-center px-4">
          <h3 className="text-2xl font-black italic tracking-tighter uppercase">Scoreboard</h3>
          {myPlayer && (
            <div className="flex gap-4">
              {myPlayer.streak > 1 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-black flex items-center gap-1 shadow-lg"
                >
                  🔥 {myPlayer.streak} Streak
                </motion.div>
              )}
              {rankImproved && (
                <motion.div
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  className="bg-neon-blue text-white px-3 py-1 rounded-full text-xs font-black shadow-lg"
                >
                  Rank Up!
                </motion.div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3 relative">
          <AnimatePresence>
            {sortedPlayers.map((p, idx) => {
              const isMe = p.id === clientState.myId;
              const isConfirming = confirmKickId === p.id;
              return (
                <motion.div
                  layout
                  key={p.id}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  className={`glass p-4 rounded-2xl flex items-center justify-between border-l-8 transition-all relative group ${
                    isMe ? 'border-neon-purple bg-neon-purple/10' : 'border-neon-blue'
                  } ${idx < 3 ? 'scale-105 z-10' : 'opacity-80 scale-95'} ${
                    isConfirming ? 'border-red-600 bg-red-600/10' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-xl font-black text-gray-500 w-6">{idx + 1}</span>
                    <div className="relative">
                      <span className="text-4xl">{p.avatar}</span>
                      {p.streak > 2 && (
                        <motion.span
                          animate={{ scale: [1, 1.2, 1], rotate: [0, 5, -5, 0] }}
                          transition={{ repeat: Infinity, duration: 1 }}
                          className="absolute -top-2 -right-2 text-xl"
                        >
                          🔥
                        </motion.span>
                      )}
                    </div>
                    <div>
                      <h4 className="font-black text-lg">{p.name}</h4>
                      {p.lastPointsGained > 0 && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.5, y: 10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          className="text-green-400 font-black text-4xl block mt-1 drop-shadow-[0_0_15px_rgba(74,222,128,0.4)]"
                        >
                          +{p.lastPointsGained}
                        </motion.div>
                      )}
                    </div>
                  </div>

                  <div className="text-right flex items-center gap-4">
                    <div className="font-black text-2xl">{p.score}</div>
                    {isHost && p.id !== gameState.hostId && (
                      <button
                        onClick={() => handleKickClick(p.id)}
                        className={`h-8 px-2 rounded-full flex items-center justify-center font-black text-[10px] shadow-lg transition-all ${
                          isConfirming
                            ? 'bg-red-600 text-white w-auto opacity-100'
                            : 'bg-red-600 text-white w-8 opacity-0 group-hover:opacity-100'
                        }`}
                      >
                        {isConfirming ? 'KICK?' : '✕'}
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {/* Next Button */}
      {isHost && (
        <button
          onClick={nextQuestion}
          className="bg-neon-purple w-full py-6 rounded-3xl font-black text-2xl shadow-xl hover:scale-105 active:scale-95 transition-all mt-4"
        >
          {gameState.currentQuestionIndex + 1 >= gameState.questions.length ? 'VIEW WINNER' : 'NEXT QUESTION'}
        </button>
      )}

      {!isHost && (
        <p className="text-neon-purple font-black text-lg animate-pulse italic">
          Host is moving to the next round...
        </p>
      )}
    </motion.div>
  );
};

export default ResultView;
